import { extendedIdTokenClaims } from '@rivenai/core-kit';
import { z } from 'zod';
import { messageRateLimitOverrideGuard, } from '../../consts/message-rate-limit.js';
import { RivenAuthInlineHookKey, inlineHookGuard } from './inline-hook.js';
import { accessTokenJwtCustomizerGuard, clientCredentialsJwtCustomizerGuard, } from './jwt-customizer.js';
export * from './oidc-provider.js';
export * from './jwt-customizer.js';
export * from './inline-hook.js';
/**
 * Riven Auth OIDC signing key types, used mainly in REST API routes.
 */
export var RivenAuthOidcConfigKeyType;
(function (RivenAuthOidcConfigKeyType) {
    RivenAuthOidcConfigKeyType["PrivateKeys"] = "private-keys";
    RivenAuthOidcConfigKeyType["CookieKeys"] = "cookie-keys";
})(RivenAuthOidcConfigKeyType || (RivenAuthOidcConfigKeyType = {}));
/**
 * Value maps to config key names in `riven_auth_configs` table (formerly `riven_auth_configs`). Used mainly in DB SQL related scenarios.
 */
export var RivenAuthOidcConfigKey;
(function (RivenAuthOidcConfigKey) {
    RivenAuthOidcConfigKey["PrivateKeys"] = "oidc.privateKeys";
    RivenAuthOidcConfigKey["CookieKeys"] = "oidc.cookieKeys";
    RivenAuthOidcConfigKey["Session"] = "oidc.session";
})(RivenAuthOidcConfigKey || (RivenAuthOidcConfigKey = {}));
/**
 * Riven Auth supported signing key algorithms for OIDC private keys that sign JWT tokens.
 */
export var SupportedSigningKeyAlgorithm;
(function (SupportedSigningKeyAlgorithm) {
    SupportedSigningKeyAlgorithm["RSA"] = "RSA";
    SupportedSigningKeyAlgorithm["EC"] = "EC";
})(SupportedSigningKeyAlgorithm || (SupportedSigningKeyAlgorithm = {}));
export const oidcConfigKeyGuard = z.object({
    id: z.string(),
    value: z.string(),
    createdAt: z.number(),
});
export var OidcSigningKeyStatus;
(function (OidcSigningKeyStatus) {
    OidcSigningKeyStatus["Next"] = "Next";
    OidcSigningKeyStatus["Current"] = "Current";
    OidcSigningKeyStatus["Previous"] = "Previous";
})(OidcSigningKeyStatus || (OidcSigningKeyStatus = {}));
export const oidcPrivateKeyGuard = oidcConfigKeyGuard.extend({
    status: z.nativeEnum(OidcSigningKeyStatus).optional(),
});
export const oidcSessionConfigGuard = z.object({
    ttl: z.number().int().min(1).max(31_536_000).optional(),
});
export const rivenAuthOidcConfigGuard = Object.freeze({
    [RivenAuthOidcConfigKey.PrivateKeys]: oidcPrivateKeyGuard.array(),
    [RivenAuthOidcConfigKey.CookieKeys]: oidcConfigKeyGuard.array(),
    // Session config is optional, if not set, it will fallback to default value in core.
    [RivenAuthOidcConfigKey.Session]: oidcSessionConfigGuard.nullish().transform((data) => data ?? {}),
});
export var RivenAuthJwtTokenKey;
(function (RivenAuthJwtTokenKey) {
    RivenAuthJwtTokenKey["AccessToken"] = "jwt.accessToken";
    RivenAuthJwtTokenKey["ClientCredentials"] = "jwt.clientCredentials";
})(RivenAuthJwtTokenKey || (RivenAuthJwtTokenKey = {}));
export const jwtCustomizerConfigGuard = Object.freeze({
    [RivenAuthJwtTokenKey.AccessToken]: accessTokenJwtCustomizerGuard,
    [RivenAuthJwtTokenKey.ClientCredentials]: clientCredentialsJwtCustomizerGuard,
});
export const inlineHookConfigGuard = Object.freeze({
    [RivenAuthInlineHookKey.PostFirstFactorVerification]: inlineHookGuard,
    [RivenAuthInlineHookKey.PostSignIn]: inlineHookGuard,
});
export const jwtCustomizerConfigsGuard = z.discriminatedUnion('key', [
    z.object({
        key: z.literal(RivenAuthJwtTokenKey.AccessToken),
        value: accessTokenJwtCustomizerGuard,
    }),
    z.object({
        key: z.literal(RivenAuthJwtTokenKey.ClientCredentials),
        value: clientCredentialsJwtCustomizerGuard,
    }),
]);
/* --- Riven Auth tenant configs --- */
export const adminConsoleDataGuard = z.object({
    signInExperienceCustomized: z.boolean(),
    organizationCreated: z.boolean(),
    developmentTenantMigrationNotification: z
        .object({
        isPaidTenant: z.boolean(),
        /**
         * Tag is used to store the original tenant tag before dev tenant migration.
         * This field is only used for DB rollback and because the `TenantTag` may change, so we don't guard it as the `TenantTag` type.
         */
        tag: z.string(),
        readAt: z.number().optional(),
    })
        .optional(),
    checkedChargeNotification: z
        .object({
        token: z.boolean().optional(),
        apiResource: z.boolean().optional(),
        machineToMachineApp: z.boolean().optional(),
        tenantMember: z.boolean().optional(),
    })
        .optional(),
});
/* --- Riven Auth tenant cloud connection config --- */
export const cloudConnectionDataGuard = z.object({
    appId: z.string(),
    appSecret: z.string(),
    resource: z.string(),
});
/* --- ID Token Config --- */
export const extendedIdTokenClaimsGuard = z.enum(extendedIdTokenClaims);
export const idTokenConfigGuard = z.object({
    enabledExtendedClaims: extendedIdTokenClaimsGuard.array().optional(),
});
export const signingKeyRotationStateGuard = z.object({
    tenantCacheExpiresAt: z.number().optional(),
    signingKeyRotationAt: z.number().optional(),
});
export var RivenAuthTenantConfigKey;
(function (RivenAuthTenantConfigKey) {
    RivenAuthTenantConfigKey["AdminConsole"] = "adminConsole";
    RivenAuthTenantConfigKey["CloudConnection"] = "cloudConnection";
    /** The URL to redirect when session not found in Sign-in Experience. */
    RivenAuthTenantConfigKey["SessionNotFoundRedirectUrl"] = "sessionNotFoundRedirectUrl";
    /** ID token configuration for extended claims. */
    RivenAuthTenantConfigKey["IdToken"] = "idToken";
    /** Tenant-scoped rotation state for staged private signing key activation. */
    RivenAuthTenantConfigKey["SigningKeyRotationState"] = "signingKeyRotationState";
    /** Internal, ops-only override of the system message send-rate-limit policy. Not exposed by any API. */
    RivenAuthTenantConfigKey["MessageRateLimitOverride"] = "messageRateLimitOverride";
})(RivenAuthTenantConfigKey || (RivenAuthTenantConfigKey = {}));
export const rivenAuthTenantConfigGuard = Object.freeze({
    [RivenAuthTenantConfigKey.AdminConsole]: adminConsoleDataGuard,
    [RivenAuthTenantConfigKey.CloudConnection]: cloudConnectionDataGuard,
    [RivenAuthTenantConfigKey.SessionNotFoundRedirectUrl]: z.object({ url: z.string() }),
    [RivenAuthTenantConfigKey.IdToken]: idTokenConfigGuard,
    [RivenAuthTenantConfigKey.SigningKeyRotationState]: signingKeyRotationStateGuard,
    [RivenAuthTenantConfigKey.MessageRateLimitOverride]: messageRateLimitOverrideGuard,
});
export const rivenAuthConfigKeys = Object.freeze([
    ...Object.values(RivenAuthOidcConfigKey),
    ...Object.values(RivenAuthJwtTokenKey),
    ...Object.values(RivenAuthInlineHookKey),
    ...Object.values(RivenAuthTenantConfigKey),
]);
export const rivenAuthConfigGuards = Object.freeze({
    ...rivenAuthOidcConfigGuard,
    ...jwtCustomizerConfigGuard,
    ...inlineHookConfigGuard,
    ...rivenAuthTenantConfigGuard,
});
export const oidcConfigKeysResponseGuard = oidcConfigKeyGuard.omit({ value: true }).merge(z.object({
    signingKeyAlgorithm: z.nativeEnum(SupportedSigningKeyAlgorithm).optional(),
    status: z.nativeEnum(OidcSigningKeyStatus).optional(),
    effectiveAt: z.number().optional(),
}));
